import { Text, useGLTF, Billboard, Html } from "@react-three/drei";
import { useMemo, memo } from "react";
import { Vector3 } from "three";
import { useMapRouteStore } from "@/stores/mapRouteStore";
import { FLOOR_HEIGHT } from "../../constants/camera";
import { areVector3Equal } from "@/utils/memoComparators";

type RoomLabelData = {
  name: string;
  position: Vector3;
  floor: number;
  meshName: string;
  roomId: string;
};

const LABEL_Y_OFFSET = 0.5;

// Отдельный компонент для одного лейбла
const RoomLabel = memo(
  function RoomLabel({
    name,
    position,
    isEndpoint,
    onClick,
  }: {
    name: string;
    position: Vector3;
    isEndpoint: boolean;
    onClick: () => void;
  }) {
    // Для endpoint лейблов используем Html (всегда поверх WebGL)
    if (isEndpoint) {
      return (
        <group position={position}>
          <Html
            center
            distanceFactor={8}
            style={{
              fontSize: "44px",
              fontWeight: "bold",
              color: "#00205B",
              whiteSpace: "nowrap",
              pointerEvents: "auto",
              cursor: "pointer",
              userSelect: "none",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            {name}
          </Html>
        </group>
      );
    }

    // Обычные лейблы - Three.js Text
    return (
      <group position={position}>
        <Billboard follow={true} lockX={false} lockY={false} lockZ={false}>
          <Text
            fontSize={0.25}
            color="#00205B"
            anchorX="center"
            anchorY="middle"
            outlineWidth={0.01}
            outlineColor="#ffffff"
            renderOrder={1001}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            onPointerEnter={() => {
              document.body.style.cursor = "pointer";
            }}
            onPointerLeave={() => {
              document.body.style.cursor = "default";
            }}
          >
            {name}
          </Text>
        </Billboard>
      </group>
    );
  },
  (prev, next) =>
    prev.name === next.name &&
    prev.isEndpoint === next.isEndpoint &&
    areVector3Equal(prev.position, next.position),
);

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * АЛЬТЕРНАТИВНЫЙ ПОДХОД: Невидимый mesh hitbox
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Этот подход использует отдельный невидимый plane mesh для обработки кликов
 * вместо навешивания событий напрямую на Text компонент.
 *
 * ПЛЮСЫ:
 * ✓ Raycasting на plane mesh в ~10 раз быстрее чем на Text геометрию
 *   (~0.01-0.05ms vs ~0.1-0.3ms на один raycast)
 * ✓ meshBounds оптимизирует проверку через bounding box (еще быстрее)
 * ✓ Подходит для большого количества интерактивных лейблов (500+ объектов)
 * ✓ Простая геометрия (plane = всего 2 треугольника)
 *
 * МИНУСЫ:
 * ✗ Больше кода (нужно управлять двумя отдельными объектами)
 * ✗ Нужно вручную синхронизировать размер hitbox с длиной текста
 * ✗ Чуть сложнее для понимания
 *
 * КОГДА ИСПОЛЬЗОВАТЬ:
 * → Если производительность становится проблемой (>500 интерактивных лейблов)
 * → Если профилирование показывает узкое место в raycasting
 * → Если нужна максимальная оптимизация для большого количества объектов
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * РЕАЛИЗАЦИЯ:
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * function RoomLabel({ name, position, onClick }) {
 *   return (
 *     <group position={position}>
 *       <Billboard follow={true} lockX={false} lockY={false} lockZ={false}>
 *         <Text
 *           fontSize={0.25}
 *           color="#00205B"
 *           anchorX="center"
 *           anchorY="middle"
 *           outlineWidth={0.01}
 *           outlineColor="#ffffff"
 *           renderOrder={1001}
 *         >
 *           {name}
 *         </Text>
 *       </Billboard>
 *
 *       Невидимый hitbox для быстрого raycasting
 *       <mesh
 *         raycast={meshBounds}
 *         onClick={(e) => {
 *           e.stopPropagation();
 *           onClick();
 *         }}
 *         onPointerEnter={() => {
 *           document.body.style.cursor = "pointer";
 *         }}
 *         onPointerLeave={() => {
 *           document.body.style.cursor = "default";
 *         }}
 *       >
 *         <planeGeometry args={[name.length * 0.15 + 0.5, 0.4]} />
 *         <meshBasicMaterial transparent opacity={0} depthTest={false} />
 *       </mesh>
 *     </group>
 *   );
 * }
 *
 * ВАЖНО: Также нужно вернуть импорт meshBounds:
 * import { Text, meshBounds, useGLTF, Billboard } from "@react-three/drei";
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

export default function RoomLabels({
  onLabelClick,
}: {
  onLabelClick?: (labelData: RoomLabelData) => void;
}) {
  const { scene } = useGLTF("/models/1.11.glb");
  const currentFloor = useMapRouteStore((s) => s.currentFloor);
  const direction = useMapRouteStore((s) => s.direction);
  const selectedSegmentId = useMapRouteStore((s) => s.selectedSegmentId);
  const isFloorManuallySelected = useMapRouteStore(
    (s) => s.isFloorManuallySelected,
  );

  const roomLabels = useMemo<RoomLabelData[]>(() => {
    const labels: RoomLabelData[] = [];

    scene.traverse((obj) => {
      // Исключаем точки входа (entry points)
      if (obj.name.includes("entry")) return;

      // Ищем point_room_* объекты (центры комнат)
      if (!obj.name.startsWith("point_room_")) return;

      // Получаем parent для roomId
      const parentName = obj.parent?.name;
      if (!parentName?.startsWith("room_")) return;

      // Получаем мировую позицию
      const worldPos = new Vector3();
      obj.getWorldPosition(worldPos);

      // Определяем этаж по Y координате
      const floor = Math.round(worldPos.y / FLOOR_HEIGHT) + 1;

      // Добавляем смещение вверх
      const labelPosition = worldPos.clone();
      labelPosition.y += LABEL_Y_OFFSET;

      labels.push({
        name: getLabelFromMeshName(obj.name),
        position: labelPosition,
        floor,
        meshName: obj.name,
        roomId: parentName,
      });
    });

    return labels;
  }, [scene]);

  // Вычисляем видимые этажи в зависимости от состояния маршрута
  const visibleFloors = useMemo<Set<number>>(() => {
    // Нет маршрута → только currentFloor
    if (!direction) {
      return new Set([currentFloor]);
    }

    // Выбран сегмент → этаж сегмента (приоритет выше isFloorManuallySelected)
    if (selectedSegmentId !== null) {
      const segment = direction.segmentationRoute.segments.find(
        (s) => s.id === selectedSegmentId,
      );
      return new Set([segment?.floor ?? currentFloor]);
    }

    // Вручную выбран этаж → currentFloor
    if (isFloorManuallySelected) {
      return new Set([currentFloor]);
    }

    // Весь маршрут → этаж первого и последнего сегмента
    const segments = direction.segmentationRoute.segments;
    if (segments.length === 0) {
      return new Set([currentFloor]);
    }

    const firstFloor = segments[0].floor;
    const lastFloor = segments[segments.length - 1].floor;

    // Если маршрут на одном этаже - только он
    if (firstFloor === lastFloor) {
      return new Set([firstFloor]);
    }

    return new Set([firstFloor, lastFloor]);
  }, [direction, selectedSegmentId, isFloorManuallySelected, currentFloor]);

  // Фильтруем по видимым этажам
  const visibleLabels = useMemo(
    () => roomLabels.filter((label) => visibleFloors.has(label.floor)),
    [roomLabels, visibleFloors],
  );

  // Вычисляем endpoint комнаты (начальная и конечная точки маршрута)
  const endpointRoomIds = useMemo<Set<string>>(() => {
    if (!direction || selectedSegmentId !== null) {
      return new Set();
    }

    const points = direction.points;
    if (points.length === 0) return new Set();

    // Используем первую и последнюю точку
    // const startPoint = points[0];
    // const endPoint = points[points.length - 1];

    const startPoint = points[0];
    const endPoint = points[points.length - 1];

    const result = new Set<string>();

    const isRoom = (parent: string): boolean => {
      const p = parent.toLowerCase();
      return (
        p.startsWith("room_") ||
        p === "room_library" ||
        p.startsWith("room_wardrobe")
      );
    };

    // console.log("Start point:", startPoint, isRoom(startPoint.parent));
    // console.log("End point:", endPoint, isRoom(endPoint.parent));
    if (startPoint && isRoom(startPoint.parent)) {
      result.add(startPoint.parent);
    }
    if (endPoint && isRoom(endPoint.parent)) {
      result.add(endPoint.parent);
    }

    return result;
  }, [direction, selectedSegmentId]);

  return (
    <group>
      {visibleLabels.map((label) => (
        <RoomLabel
          key={label.meshName}
          name={label.name}
          position={label.position}
          isEndpoint={endpointRoomIds.has(label.roomId)}
          onClick={() => {
            console.log("Clicked room:", label.name);
            onLabelClick?.(label);
          }}
        />
      ))}
    </group>
  );
}

function getLabelFromMeshName(meshName: string): string {
  // Убираем префикс "point_room_"
  const withoutPrefix = meshName.replace("point_room_", "");

  // Гардеробы: wardrobe_1 → "Гардероб 1"
  if (withoutPrefix.startsWith("wardrobe_")) {
    const number = withoutPrefix.replace("wardrobe_", "");
    return `Гардероб ${number}`;
  }

  // Библиотека
  if (withoutPrefix === "library") {
    return "Библиотека";
  }

  // Стандартные комнаты: 299 → "Кабинет 299"
  return `Кабинет ${withoutPrefix}`;
}
